import './TagsMainMenu.scss';

import { AMPLITUDE_PERISCOPE_EVENT_SOURCE } from '@sonar-software/sonar-analytics';
import { DarkTheme } from '@ui/themes';
import { haltClickEventPropagation } from '@lib/events';
import { Link } from 'react-router-dom';
import { Row } from 'antd';
import { SearchInput } from '@ui/general';
import { SettingOutlined } from '@ant-design/icons';
import { SonarMenuItem } from '@ui/menu';
import { SonarObjectType } from '@sonar-software/sonar-ts-types';
import { SonarTagDisplay } from '@ui/tags';
import { SonarTag as SonarTagType } from '@sonar-software/sonar-core';

import {
	COPY_SONAR_OBJECT_BASE_PLURAL,
	FULL_ROUTES,
	PLACEHOLDERS_COPY
} from '@sonar-software/sonar-ts-constants';
import React, { useCallback, useMemo, useState } from 'react';
import { useApplicationSidebar, useCurrentUser, usePeriscopeActions, useTags } from '@hooks';

export interface TagsMainMenuProps {
	isCollapsed: boolean;
}

function buildSubMenuItem(
	tags: Array<SonarTagType>,
	isReadOnly: boolean,
	isCollapsed: boolean,
	handleTagClick: (tag: SonarTagType) => void,
	handleSearchChange:(filter: string) => void,
	filterValue: string
): SonarMenuItem {
	const topLabel = isReadOnly ?
		COPY_SONAR_OBJECT_BASE_PLURAL.TAG :
		<Row justify='space-between'>
			{ COPY_SONAR_OBJECT_BASE_PLURAL.TAG }
			{ isCollapsed ?
				<SettingOutlined /> :
				<Link
					className='manager-link'
					onClick={ haltClickEventPropagation }
					to={ FULL_ROUTES.TAGS_MANAGE }>
					<SettingOutlined />
				</Link> }
		</Row>;

	const subMenuItem: SonarMenuItem = {
		children: isCollapsed ? undefined : [],
		className: 'tag-submenu',
		key: FULL_ROUTES.TAGS_MANAGE,
		label: topLabel,
		route: isCollapsed ? FULL_ROUTES.TAGS_MANAGE : undefined
	};

	if (!isCollapsed && tags?.length > 10) {
		subMenuItem.children?.push({
			className: 'tag-search-entry',
			key: 'tag-search',
			label:
	<DarkTheme>
		<div id='tag-search'>
			<SearchInput
				className='tag-search-input'
				onChange={ handleSearchChange }
				placeholder={ PLACEHOLDERS_COPY.SEARCH_TAGS } />
		</div>
	</DarkTheme>
		});
	}

	if (!isCollapsed) {
		const matchesFilter = (tag: SonarTagType) => {
			if (!filterValue) {
				return true;
			}

			return tag.name.toLowerCase().includes(filterValue.toLowerCase());
		};

		for (const tag of tags.filter(matchesFilter)) {
			subMenuItem.children?.push({
				className: 'tag-submenu-item',
				id: `tag-${tag.id}`,
				key: `tag-${tag.id}`,
				label: <SonarTagDisplay
					noBorder
					tagId={ tag.id } />,
				onClick: () => handleTagClick(tag)
			});
		}
	}

	return subMenuItem;
}

export function useTagSubMenu(): SonarMenuItem {
	const { isClosed } = useApplicationSidebar();
	const [ filterValue, setFilterValue ] = useState('');

	const tags = useTags();
	const { startPeriscope } = usePeriscopeActions();
	const { isReadOnly } = useCurrentUser();

	const handleTagClick = useCallback(
		(tag: SonarTagType) => {
			startPeriscope({
				from: AMPLITUDE_PERISCOPE_EVENT_SOURCE.MAIN_MENU,
				id: tag.id,
				name: tag.name,
				type: SonarObjectType.SonarTag
			});
		},
		[ startPeriscope ]
	);

	const subMenu: SonarMenuItem = useMemo(
		() => buildSubMenuItem(tags, isReadOnly, isClosed, handleTagClick, setFilterValue, filterValue),
		[ filterValue, handleTagClick, isClosed, isReadOnly, tags ]
	);

	return subMenu;
}
